/**
 * Curated top-N job category ("field" facet) config for /jobs/category/{slug} pages.
 *
 * `field` is LLM-assigned free text (not an enum) — noisy, with near-duplicate spelling
 * variants. Each category lists every raw `field` value it should absorb via OR-filtering,
 * PER LOCALE: each Typesense collection stores `field` in its own language, so English-only
 * values match ~nothing on jobs_fi/jobs_sv (that bug shipped with phase 1 — the fi/sv
 * category pages served 2–6 jobs while en served 1,100+).
 *
 * fi/sv values were read from live Typesense facet counts per collection (Aug 2026) and
 * verified by cross-locale document joins on shared job ids where translations were
 * ambiguous. Typesense `:=` matches case-insensitively, so case-only variants are omitted.
 * Re-check periodically as the enrichment pipeline's field vocabulary drifts.
 */

import type { CategoryDefinition, SupportedLocale } from '../../types/category-hub';

export const CATEGORY_HUB_CONFIG: CategoryDefinition[] = [
  {
    slug: 'software-developer',
    fieldValues: {
      en: ['Software engineering', 'Software Developer'],
      fi: ['Ohjelmistokehitys', 'Ohjelmistotekniikka', 'Ohjelmistokehittäjä'],
      sv: ['Mjukvaruutveckling', 'Programvaruutveckling', 'Programvaruteknik', 'Mjukvaruteknik'],
    },
    displayNames: {
      en: 'Software Developer',
      fi: 'Ohjelmistokehittäjä',
      sv: 'Mjukvaruutvecklare',
    },
    relatedCategorySlugs: ['technology'],
  },
  {
    slug: 'sales',
    fieldValues: {
      en: ['Sales'],
      fi: ['Myynti'],
      sv: ['Försäljning'],
    },
    displayNames: {
      en: 'Sales',
      fi: 'Myynti',
      sv: 'Försäljning',
    },
    relatedCategorySlugs: ['sales-assistant', 'marketing', 'customer-service'],
  },
  {
    slug: 'construction',
    fieldValues: {
      // First value per locale is the processor's current field enum (locale-enum-fields.ts
      // FIELD_EN/FI/SV); the rest are the pre-enum free-text spellings older jobs still carry.
      en: ['Construction & trades', 'Construction'],
      fi: ['Rakennus- ja asennusala', 'Rakentaminen', 'Rakennusala'],
      sv: ['Bygg & installation', 'Bygg och anläggning', 'Bygg'],
    },
    displayNames: {
      en: 'Construction',
      fi: 'Rakennusala',
      sv: 'Byggbranschen',
    },
    relatedCategorySlugs: ['house-builder', 'carpenter', 'electrician', 'civil-engineering-labourer', 'manufacturing'],
  },
  {
    slug: 'healthcare-assistant',
    fieldValues: {
      en: ['Healthcare Assistant', 'Health Care Assistants', 'Nurse Assistant'],
      fi: ['Lähihoitaja', 'Lähihoitajat', 'Kotihoidon Lähihoitaja', 'Hoiva-Avustaja'],
      sv: ['Undersköterska, Vård- Och Specialavdelning Och Mottagning', 'Undersköterska', 'Närvårdare'],
    },
    displayNames: {
      en: 'Healthcare Assistant',
      fi: 'Hoiva-avustaja',
      sv: 'Vårdassistent',
    },
    relatedCategorySlugs: ['healthcare', 'nurse'],
  },
  {
    slug: 'technology',
    fieldValues: {
      en: ['Technology'],
      fi: ['Teknologia'],
      sv: ['Teknologi'],
    },
    displayNames: {
      en: 'Technology',
      fi: 'Teknologia',
      sv: 'Teknik',
    },
    relatedCategorySlugs: ['software-developer', 'data-analytics'],
  },
  {
    slug: 'cook',
    fieldValues: {
      en: ['Cook'],
      fi: ['Kokki'],
      sv: ['Kokerska'],
    },
    displayNames: {
      en: 'Cook',
      fi: 'Kokki',
      sv: 'Kock',
    },
    relatedCategorySlugs: ['waiter-waitress', 'hospitality'],
  },
  {
    slug: 'building-cleaner',
    fieldValues: {
      en: ['Building Cleaner'],
      fi: ['Laitossiivooja'],
      sv: ['Fastighetsstädare'],
    },
    displayNames: {
      en: 'Building Cleaner',
      fi: 'Kiinteistönhuoltaja',
      sv: 'Fastighetsstädare',
    },
    relatedCategorySlugs: ['construction', 'cleaning'],
  },
  {
    slug: 'personal-assistant',
    fieldValues: {
      en: ['Personal Assistant'],
      fi: ['Henkilökohtainen Avustaja'],
      sv: ['Personlig Assistent'],
    },
    displayNames: {
      en: 'Personal Assistant',
      fi: 'Henkilökohtainen avustaja',
      sv: 'Personlig assistent',
    },
    relatedCategorySlugs: ['healthcare-assistant'],
  },
  {
    slug: 'welder',
    fieldValues: {
      en: ['Welder'],
      fi: ['Hitsaaja'],
      sv: ['Svetsare, Manuell'],
    },
    displayNames: {
      en: 'Welder',
      fi: 'Hitsaaja',
      sv: 'Svetsare',
    },
    relatedCategorySlugs: ['construction', 'electrician', 'manufacturing'],
  },
  {
    slug: 'sales-assistant',
    fieldValues: {
      en: ['Sales Assistant'],
      fi: ['Myyjä'],
      sv: ['Försäljningsassistent'],
    },
    displayNames: {
      en: 'Sales Assistant',
      fi: 'Myyjä',
      sv: 'Säljassistent',
    },
    relatedCategorySlugs: ['sales', 'retail'],
  },
  {
    slug: 'healthcare',
    fieldValues: {
      en: ['Healthcare'],
      fi: ['Sosiaali- ja terveysala', 'Terveysala', 'Terveydenhuolto'],
      sv: ['Hälso- och sjukvård', 'Hälsovård'],
    },
    displayNames: {
      en: 'Healthcare',
      fi: 'Terveydenhuolto',
      sv: 'Hälsovård',
    },
    relatedCategorySlugs: ['healthcare-assistant', 'nurse'],
  },
  {
    slug: 'carpenter',
    fieldValues: {
      en: ['Carpenter', 'Carpenters And Joiners'],
      fi: ['Kirvesmies', 'Kirvesmiehet ja rakennuspuusepät'],
      sv: ['Snickare', 'Timmermän och byggnadssnickare'],
    },
    displayNames: {
      en: 'Carpenter',
      fi: 'Kirvesmies',
      sv: 'Snickare',
    },
    relatedCategorySlugs: ['construction', 'house-builder'],
  },
  {
    slug: 'house-builder',
    fieldValues: {
      en: ['House Builder', 'House Builders'],
      fi: ['Talonrakentaja', 'Talonrakentajat'],
      sv: ['Träarbetare', 'Byggnadsarbetare'],
    },
    displayNames: {
      en: 'House Builder',
      fi: 'Talonrakentaja',
      sv: 'Husbyggare',
    },
    relatedCategorySlugs: ['construction', 'carpenter'],
  },
  {
    slug: 'waiter-waitress',
    fieldValues: {
      en: ['Waiter/waitress', 'Waiters'],
      fi: ['Tarjoilija', 'Tarjoilijat'],
      sv: ['Servitör/servitris'],
    },
    displayNames: {
      en: 'Waiter/Waitress',
      fi: 'Tarjoilija',
      sv: 'Servitör/servitris',
    },
    relatedCategorySlugs: ['cook', 'hospitality'],
  },
  {
    slug: 'building-construction-worker',
    fieldValues: {
      en: ['Building Construction Worker', 'Building Construction Labourers'],
      fi: ['Rakennustyömaan Avustava Työntekijä', 'Rakennusalan Avustavat Työntekijät'],
      sv: ['Medhjälpare, Byggnadsverksamhet'],
    },
    displayNames: {
      en: 'Building Construction Worker',
      fi: 'Rakennustyöntekijä',
      sv: 'Byggnadsarbetare',
    },
    relatedCategorySlugs: ['construction', 'civil-engineering-labourer'],
  },
  {
    slug: 'electrician',
    fieldValues: {
      en: ['Electrician'],
      fi: ['Sähköasentaja'],
      sv: ['Installationselektriker'],
    },
    displayNames: {
      en: 'Electrician',
      fi: 'Sähköasentaja',
      sv: 'Elektriker',
    },
    relatedCategorySlugs: ['construction', 'welder'],
  },
  {
    slug: 'civil-engineering-labourer',
    fieldValues: {
      en: ['Civil Engineering Labourers', 'Civil Engineering Worker'],
      fi: ['Maa- Ja Vesirakentamisen Avustavat Työntekijät', 'Maa- Ja Vesirakentamisen Avustava Työntekijä'],
      sv: ['Medhjälpare, Anläggningsverksamhet', 'Anläggningsarbetare'],
    },
    displayNames: {
      en: 'Civil Engineering Labourer',
      fi: 'Maanrakennustyöntekijä',
      sv: 'Anläggningsarbetare',
    },
    relatedCategorySlugs: ['construction', 'excavator-operator'],
  },
  {
    slug: 'excavator-operator',
    fieldValues: {
      en: ['Excavator Operator'],
      fi: ['Kaivinkoneenkuljettaja'],
      sv: ['Grävmaskinsförare'],
    },
    displayNames: {
      en: 'Excavator Operator',
      fi: 'Kaivinkoneenkuljettaja',
      sv: 'Grävmaskinist',
    },
    relatedCategorySlugs: ['civil-engineering-labourer', 'construction'],
  },
  {
    slug: 'nurse',
    fieldValues: {
      en: ['Nurse Responsible For General Care'],
      fi: ['Sairaanhoitaja'],
      sv: ['Sjuksköterska, Grundutbildad'],
    },
    displayNames: {
      en: 'Nurse',
      fi: 'Sairaanhoitaja',
      sv: 'Sjukskötare',
    },
    relatedCategorySlugs: ['healthcare', 'healthcare-assistant'],
  },
  {
    slug: 'finance-accounting',
    fieldValues: {
      en: ['Finance & accounting'],
      fi: ['Talous ja kirjanpito'],
      sv: ['Ekonomi & redovisning', 'Ekonomi och redovisning'],
    },
    displayNames: {
      en: 'Finance & Accounting',
      fi: 'Talous ja kirjanpito',
      sv: 'Ekonomi och redovisning',
    },
    relatedCategorySlugs: ['sales', 'technology', 'legal'],
  },
  // Broad field categories added 2026-09-29: one per processor field enum value
  // (locale-enum-fields.ts FIELD_EN/FI/SV) that had no category page, each with measured Finnish
  // search demand ("hr työpaikat" 1.4k/mo, "logistiikka työpaikat" 1.3k/mo, ... Ahrefs FI).
  {
    slug: 'human-resources',
    fieldValues: {
      en: ['Human resources'],
      fi: ['Henkilöstö'],
      sv: ['HR'],
    },
    displayNames: {
      en: 'Human Resources',
      fi: 'HR ja henkilöstöhallinto',
      sv: 'HR och personal',
    },
    relatedCategorySlugs: ['finance-accounting', 'legal', 'marketing'],
  },
  {
    slug: 'logistics',
    fieldValues: {
      en: ['Operations & supply chain'],
      fi: ['Logistiikka ja osto'],
      sv: ['Logistik & inköp'],
    },
    displayNames: {
      en: 'Logistics & Supply Chain',
      fi: 'Logistiikka ja varastotyö',
      sv: 'Logistik och lager',
    },
    relatedCategorySlugs: ['manufacturing', 'retail', 'construction'],
  },
  {
    slug: 'marketing',
    fieldValues: {
      en: ['Marketing'],
      fi: ['Markkinointi'],
      sv: ['Marknadsföring'],
    },
    displayNames: {
      en: 'Marketing',
      fi: 'Markkinointi',
      sv: 'Marknadsföring',
    },
    relatedCategorySlugs: ['sales', 'design', 'data-analytics'],
  },
  {
    slug: 'legal',
    fieldValues: {
      en: ['Legal'],
      fi: ['Lakiala'],
      sv: ['Juridik'],
    },
    displayNames: {
      en: 'Legal',
      fi: 'Lakiala',
      sv: 'Juridik',
    },
    relatedCategorySlugs: ['finance-accounting', 'human-resources'],
  },
  {
    slug: 'customer-service',
    fieldValues: {
      en: ['Customer support'],
      fi: ['Asiakaspalvelu'],
      sv: ['Kundsupport'],
    },
    displayNames: {
      en: 'Customer Service',
      fi: 'Asiakaspalvelu',
      sv: 'Kundtjänst',
    },
    relatedCategorySlugs: ['sales', 'retail', 'hospitality'],
  },
  {
    slug: 'security',
    fieldValues: {
      en: ['Security services'],
      fi: ['Vartiointi ja turvallisuus'],
      sv: ['Säkerhetstjänster'],
    },
    displayNames: {
      en: 'Security Services',
      fi: 'Vartiointi ja turvallisuus',
      sv: 'Säkerhetstjänster',
    },
    relatedCategorySlugs: ['cleaning', 'customer-service'],
  },
  {
    slug: 'design',
    fieldValues: {
      en: ['Design'],
      fi: ['Design'],
      sv: ['Design'],
    },
    displayNames: {
      en: 'Design',
      fi: 'Design ja graafinen suunnittelu',
      sv: 'Design',
    },
    relatedCategorySlugs: ['marketing', 'product-management', 'software-developer'],
  },
  {
    slug: 'cleaning',
    fieldValues: {
      en: ['Cleaning & property services'],
      fi: ['Siivous- ja kiinteistöpalvelut'],
      sv: ['Städ & fastighetsservice'],
    },
    displayNames: {
      en: 'Cleaning & Property Services',
      fi: 'Siivous ja kiinteistöpalvelut',
      sv: 'Städ och fastighetsservice',
    },
    relatedCategorySlugs: ['building-cleaner', 'security', 'hospitality'],
  },
  {
    slug: 'education',
    fieldValues: {
      en: ['Education'],
      fi: ['Koulutusala'],
      sv: ['Utbildning'],
    },
    displayNames: {
      en: 'Education & Teaching',
      fi: 'Opetus ja koulutus',
      sv: 'Utbildning och undervisning',
    },
    relatedCategorySlugs: ['healthcare', 'personal-assistant'],
  },
  {
    slug: 'data-analytics',
    fieldValues: {
      en: ['Data & analytics'],
      fi: ['Data ja analytiikka'],
      sv: ['Data & analys'],
    },
    displayNames: {
      en: 'Data & Analytics',
      fi: 'Data ja analytiikka',
      sv: 'Data och analys',
    },
    relatedCategorySlugs: ['software-developer', 'technology', 'finance-accounting'],
  },
  {
    slug: 'product-management',
    fieldValues: {
      en: ['Product'],
      fi: ['Tuote'],
      sv: ['Produkt'],
    },
    displayNames: {
      en: 'Product Management',
      fi: 'Tuotehallinta',
      sv: 'Produktledning',
    },
    relatedCategorySlugs: ['software-developer', 'design', 'marketing'],
  },
  {
    slug: 'hospitality',
    fieldValues: {
      en: ['Hospitality & food service'],
      fi: ['Ravintola- ja majoitusala'],
      sv: ['Restaurang & hotell'],
    },
    displayNames: {
      en: 'Restaurants & Hospitality',
      fi: 'Ravintola- ja majoitusala',
      sv: 'Restaurang och hotell',
    },
    relatedCategorySlugs: ['cook', 'waiter-waitress', 'cleaning'],
  },
  {
    slug: 'retail',
    fieldValues: {
      en: ['Retail'],
      fi: ['Vähittäiskauppa'],
      sv: ['Detaljhandel'],
    },
    displayNames: {
      en: 'Retail',
      fi: 'Kauppa ja myymälätyö',
      sv: 'Detaljhandel',
    },
    relatedCategorySlugs: ['sales-assistant', 'sales', 'customer-service'],
  },
  {
    slug: 'manufacturing',
    fieldValues: {
      en: ['Manufacturing & industrial'],
      fi: ['Teollisuus ja tuotanto'],
      sv: ['Tillverkning & industri'],
    },
    displayNames: {
      en: 'Manufacturing & Industry',
      fi: 'Teollisuus ja tuotanto',
      sv: 'Tillverkning och industri',
    },
    relatedCategorySlugs: ['welder', 'logistics', 'construction'],
  },
];

export function getAllCategorySlugs(): string[] {
  return CATEGORY_HUB_CONFIG.map((category) => category.slug);
}

/** The broad field categories (one per processor field enum value) added 2026-09-29, each with
 *  measured search demand, so srpAllowlist.ts indexes them alongside the top-10 slice. */
export const BROAD_FIELD_CATEGORY_SLUGS: ReadonlySet<string> = new Set([
  'human-resources', 'logistics', 'marketing', 'legal', 'customer-service', 'security', 'design',
  'cleaning', 'education', 'data-analytics', 'product-management', 'hospitality', 'retail', 'manufacturing',
]);

export function getCategoryBySlug(slug: string): CategoryDefinition | null {
  if (!slug) return null;
  const normalized = slug.trim().toLowerCase();
  return CATEGORY_HUB_CONFIG.find((category) => category.slug.toLowerCase() === normalized) ?? null;
}

/**
 * Reverse lookup: raw `field` facet value -> owning category slug. Used to rebuild the
 * pretty `/jobs/category/{slug}` canonical URL once a `field` active filter is resolved.
 */
export function getCategorySlugByFieldValue(fieldValue: string): string | null {
  if (!fieldValue) return null;
  const normalized = fieldValue.trim().toLowerCase();
  const match = CATEGORY_HUB_CONFIG.find((category) =>
    (Object.values(category.fieldValues) as string[][]).some((values) =>
      values.some((value) => value.toLowerCase() === normalized),
    ),
  );
  return match?.slug ?? null;
}

/** Field facet values for `locale`, falling back to English. */
export function getCategoryFieldValuesForLocale(
  category: CategoryDefinition,
  locale: SupportedLocale,
): string[] {
  return category.fieldValues[locale] ?? category.fieldValues.en;
}

export function getCategoryDisplayName(slug: string, locale: SupportedLocale): string | null {
  return getCategoryBySlug(slug)?.displayNames[locale] ?? null;
}
